import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Gauge,
  Search,
  XCircle,
} from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import { useToast } from '../../../components/common/Toast';
import {
  ErrorBanner,
  HealthCharts,
  KpiTile,
  LiveControls,
  ServiceCard,
  SkeletonGrid,
  formatLatency,
  formatRelative,
  latencyColor,
  overallBannerClass,
  statusPillClass,
} from './components';
import { fetchHealthBundle, HealthMonitorError } from './healthStatusService';
import { getServiceGroup, getServiceIcon, humanizeServiceId } from './serviceIcons';
import type {
  HealthLiveness,
  HealthService,
  HealthStatusResponse,
  IntervalSeconds,
  PollSnapshot,
  ServiceProbeStatus,
} from './types';
import {
  DEFAULT_INTERVAL,
  HISTORY_CAP,
  INTERVAL_OPTIONS,
  INTERVAL_STORAGE_KEY,
  LIVE_STORAGE_KEY,
} from './types';

type FilterChip = 'all' | 'required' | 'down';
type GroupFilter = 'all' | 'api' | 'fo' | 'infra';
type SortKey = 'id' | 'status' | 'latency';

function readLivePref(): boolean {
  const raw = localStorage.getItem(LIVE_STORAGE_KEY);
  if (raw === null) return true;
  return raw === 'true';
}

function readIntervalPref(): IntervalSeconds {
  const raw = Number(localStorage.getItem(INTERVAL_STORAGE_KEY));
  if (INTERVAL_OPTIONS.includes(raw as IntervalSeconds)) return raw as IntervalSeconds;
  return DEFAULT_INTERVAL;
}

function buildSnapshot(status: HealthStatusResponse): PollSnapshot {
  const services = status.services || [];
  const up = services.filter((s) => s.status === 'up').length;
  const down = services.filter((s) => s.status === 'down').length;
  const requiredUp = services.filter((s) => s.required && s.status === 'up').length;
  const requiredDown = services.filter((s) => s.required && s.status === 'down').length;
  const optionalDown = services.filter((s) => !s.required && s.status === 'down').length;
  const avgLatency =
    services.length === 0
      ? 0
      : services.reduce((sum, s) => sum + (s.latency_ms || 0), 0) / services.length;

  const byService: PollSnapshot['byService'] = {};
  for (const s of services) {
    byService[s.id] = { status: s.status, latency_ms: s.latency_ms };
  }

  return {
    at: Date.now(),
    overall: status.status,
    up,
    down,
    requiredUp,
    requiredDown,
    optionalDown,
    avgLatency,
    byService,
  };
}

function pushHistory(prev: PollSnapshot[], next: PollSnapshot): PollSnapshot[] {
  const out = [...prev, next];
  if (out.length > HISTORY_CAP) return out.slice(out.length - HISTORY_CAP);
  return out;
}

const HealthStatusPage: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const { showToast } = useToast();

  const [live, setLive] = useState(readLivePref);
  const [intervalSec, setIntervalSec] = useState<IntervalSeconds>(readIntervalPref);
  const [countdown, setCountdown] = useState<number>(intervalSec);

  const [health, setHealth] = useState<HealthLiveness | null>(null);
  const [status, setStatus] = useState<HealthStatusResponse | null>(null);
  const [history, setHistory] = useState<PollSnapshot[]>([]);
  const [flippedIds, setFlippedIds] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [pollError, setPollError] = useState<string | null>(null);
  const [relativeTick, setRelativeTick] = useState(0);

  const [filter, setFilter] = useState<FilterChip>('all');
  const [group, setGroup] = useState<GroupFilter>('all');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortAsc, setSortAsc] = useState(true);

  const abortRef = useRef<AbortController | null>(null);
  const liveRef = useRef(live);
  const intervalRef = useRef(intervalSec);
  const prevStatusRef = useRef<Record<string, ServiceProbeStatus>>({});
  const pollInFlightRef = useRef(false);
  const hasStatusRef = useRef(false);

  useEffect(() => {
    hasStatusRef.current = status != null;
  }, [status]);

  useEffect(() => {
    liveRef.current = live;
    localStorage.setItem(LIVE_STORAGE_KEY, String(live));
  }, [live]);

  useEffect(() => {
    intervalRef.current = intervalSec;
    localStorage.setItem(INTERVAL_STORAGE_KEY, String(intervalSec));
    setCountdown(intervalSec);
  }, [intervalSec]);

  useEffect(() => {
    const id = window.setInterval(() => setRelativeTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const applyPollResult = useCallback((bundle: { health: HealthLiveness; status: HealthStatusResponse }) => {
    const snap = buildSnapshot(bundle.status);
    const flips = new Set<string>();
    for (const svc of bundle.status.services || []) {
      const prev = prevStatusRef.current[svc.id];
      if (prev && prev !== svc.status) flips.add(svc.id);
    }
    prevStatusRef.current = Object.fromEntries(
      (bundle.status.services || []).map((s) => [s.id, s.status])
    );

    setHealth(bundle.health);
    setStatus(bundle.status);
    setHistory((h) => pushHistory(h, snap));
    setPollError(null);

    if (flips.size > 0) {
      setFlippedIds(flips);
      window.setTimeout(() => setFlippedIds(new Set()), 1200);
    }
  }, []);

  const poll = useCallback(
    async (opts?: { manual?: boolean }) => {
      if (pollInFlightRef.current && !opts?.manual) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      pollInFlightRef.current = true;
      if (!hasStatusRef.current) setLoading(true);

      try {
        const bundle = await fetchHealthBundle(controller.signal);
        if (controller.signal.aborted) return;
        applyPollResult(bundle);
        setCountdown(intervalRef.current);
      } catch (err) {
        if (controller.signal.aborted) return;
        const message =
          err instanceof HealthMonitorError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Unknown error';
        setPollError(message);
        if (opts?.manual) {
          showToast({ message, type: 'error' });
        }
      } finally {
        pollInFlightRef.current = false;
        setLoading(false);
      }
    },
    [applyPollResult, showToast]
  );

  // Initial fetch
  useEffect(() => {
    void poll();
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  // Live interval + countdown + visibility
  useEffect(() => {
    if (!live) return;

    let cancelled = false;
    setCountdown(intervalSec);

    const tick = window.setInterval(() => {
      if (cancelled) return;
      if (document.visibilityState === 'hidden') return;
      setCountdown((c) => {
        if (c <= 1) {
          void poll();
          return intervalRef.current;
        }
        return c - 1;
      });
    }, 1000);

    const onVis = () => {
      if (document.visibilityState === 'visible' && liveRef.current) {
        void poll();
        setCountdown(intervalRef.current);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [live, intervalSec, poll]);

  const services = status?.services ?? [];

  const kpis = useMemo(() => {
    const up = services.filter((s) => s.status === 'up').length;
    const down = services.filter((s) => s.status === 'down').length;
    const requiredFailing = services.filter((s) => s.required && s.status === 'down').length;
    const avg =
      services.length === 0
        ? 0
        : services.reduce((sum, s) => sum + (s.latency_ms || 0), 0) / services.length;
    const requiredUp = services.filter((s) => s.required && s.status === 'up').length;
    const optionalDown = services.filter((s) => !s.required && s.status === 'down').length;
    return { up, down, requiredFailing, avg, requiredUp, optionalDown };
  }, [services]);

  const maxLatency = useMemo(
    () => Math.max(...services.map((s) => s.latency_ms || 0), 1),
    [services]
  );

  const sparkByService = useMemo(() => {
    const map: Record<string, number[]> = {};
    for (const snap of history) {
      for (const [id, info] of Object.entries(snap.byService)) {
        if (!map[id]) map[id] = [];
        map[id].push(info.latency_ms);
      }
    }
    return map;
  }, [history]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...services];

    if (filter === 'required') list = list.filter((s) => s.required);
    if (filter === 'down') list = list.filter((s) => s.status === 'down');
    if (group !== 'all') list = list.filter((s) => getServiceGroup(s.id) === group);
    if (q) {
      list = list.filter(
        (s) => s.id.toLowerCase().includes(q) || s.url.toLowerCase().includes(q)
      );
    }

    const statusRank = (s: ServiceProbeStatus) => (s === 'down' ? 0 : s === 'up' ? 1 : 2);

    list.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'id') cmp = a.id.localeCompare(b.id);
      else if (sortKey === 'latency') cmp = a.latency_ms - b.latency_ms;
      else cmp = statusRank(a.status) - statusRank(b.status) || a.id.localeCompare(b.id);
      return sortAsc ? cmp : -cmp;
    });

    return list;
  }, [services, filter, group, query, sortKey, sortAsc]);

  const overall = status?.status ?? (pollError ? 'down' : '—');
  const requiredDownCount = kpis.requiredFailing;
  const optionalDownCount = kpis.optionalDown;

  const chipClass = (active: boolean) =>
    `px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${
      active
        ? isLight
          ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
          : 'bg-indigo-500/15 text-indigo-300 border-indigo-500/40'
        : isLight
          ? 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          : 'bg-neutral-900 text-slate-300 border-neutral-700 hover:bg-neutral-800'
    }`;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(key !== 'status');
    }
  };

  // silence unused relativeTick warning by using it in formatRelative deps visually
  void relativeTick;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={`text-xl font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>
            System Health
          </h1>
          <p className={`text-sm mt-0.5 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
            {status?.mode ? (
              <>
                Mode <span className="font-mono text-[12px]">{status.mode}</span>
                {status.checked_at && (
                  <>
                    {' '}
                    · Checked {formatRelative(status.checked_at)}
                  </>
                )}
              </>
            ) : (
              'Live probe status across platform services'
            )}
          </p>
        </div>
        <LiveControls
          live={live}
          intervalSec={intervalSec}
          countdown={countdown}
          loading={loading && status != null}
          isLight={isLight}
          onToggleLive={() => setLive((v) => !v)}
          onIntervalChange={setIntervalSec}
          onRefresh={() => void poll({ manual: true })}
        />
      </div>

      {pollError && status == null && (
        <ErrorBanner message={pollError} isLight={isLight} onRetry={() => void poll({ manual: true })} />
      )}

      {pollError && status != null && (
        <div
          className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border ${
            isLight
              ? 'bg-amber-50 border-amber-200 text-amber-700'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
          }`}
        >
          <AlertTriangle size={12} />
          Last poll failed — showing previous snapshot
        </div>
      )}

      {/* Overall banner */}
      {status && (
        <div className={`rounded-lg border px-4 py-3 ${overallBannerClass(String(overall), isLight)}`}>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <span
                className={`relative inline-flex h-3 w-3 rounded-full ${
                  overall === 'ok'
                    ? 'bg-emerald-500'
                    : overall === 'degraded'
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                }`}
              >
                {live && (
                  <span
                    className={`absolute inset-0 rounded-full animate-ping opacity-60 ${
                      overall === 'ok'
                        ? 'bg-emerald-400'
                        : overall === 'degraded'
                          ? 'bg-amber-400'
                          : 'bg-red-400'
                    }`}
                  />
                )}
              </span>
              <div>
                <p className="text-sm font-semibold capitalize tracking-wide">
                  System {String(overall)}
                </p>
                <p className="text-[11px] opacity-80 mt-0.5">
                  {requiredDownCount} required down · {optionalDownCount} optional down ·{' '}
                  {services.length} services probed
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${
                  health?.status === 'ok'
                    ? isLight
                      ? 'bg-white/70 border-emerald-200 text-emerald-800'
                      : 'bg-black/20 border-emerald-500/40 text-emerald-200'
                    : isLight
                      ? 'bg-white/70 border-red-200 text-red-800'
                      : 'bg-black/20 border-red-500/40 text-red-200'
                }`}
              >
                Monitor /health: {health?.status ?? '—'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      {(status || loading) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile label="Services up" value={status ? kpis.up : '—'} icon={CheckCircle2} accent="emerald" isLight={isLight} />
          <KpiTile label="Services down" value={status ? kpis.down : '—'} icon={XCircle} accent="red" isLight={isLight} />
          <KpiTile
            label="Required failing"
            value={status ? kpis.requiredFailing : '—'}
            icon={AlertTriangle}
            accent="amber"
            isLight={isLight}
          />
          <KpiTile
            label="Avg latency"
            value={status ? formatLatency(kpis.avg) : '—'}
            icon={Gauge}
            accent="indigo"
            isLight={isLight}
          />
        </div>
      )}

      {/* Charts */}
      {status && (
        <HealthCharts
          history={history}
          requiredUp={kpis.requiredUp}
          requiredDown={kpis.requiredFailing}
          optionalDown={kpis.optionalDown}
          isLight={isLight}
        />
      )}

      {/* Toolbar */}
      {status && (
        <div
          className={`rounded-lg border p-3 flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between ${
            isLight ? 'bg-white border-slate-200' : 'bg-neutral-900 border-neutral-800'
          }`}
        >
          <div className="flex flex-wrap items-center gap-1.5">
            {(['all', 'required', 'down'] as FilterChip[]).map((f) => (
              <button key={f} type="button" className={chipClass(filter === f)} onClick={() => setFilter(f)}>
                {f === 'all' ? 'All' : f === 'required' ? 'Required' : 'Down'}
              </button>
            ))}
            <span className={`mx-1 h-4 w-px ${isLight ? 'bg-slate-200' : 'bg-neutral-700'}`} />
            {(['all', 'api', 'fo', 'infra'] as GroupFilter[]).map((g) => (
              <button key={g} type="button" className={chipClass(group === g)} onClick={() => setGroup(g)}>
                {g === 'all' ? 'All groups' : g.toUpperCase()}
              </button>
            ))}
          </div>
          <div
            className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 w-full lg:w-64 ${
              isLight ? 'border-slate-200 bg-slate-50' : 'border-neutral-700 bg-neutral-950'
            }`}
          >
            <Search size={13} className={isLight ? 'text-slate-400' : 'text-slate-500'} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search id or URL…"
              className={`flex-1 bg-transparent text-xs outline-none ${
                isLight ? 'text-slate-800 placeholder:text-slate-400' : 'text-slate-100 placeholder:text-slate-500'
              }`}
            />
          </div>
        </div>
      )}

      {/* Cards */}
      {loading && !status ? (
        <SkeletonGrid isLight={isLight} />
      ) : filtered.length === 0 && status ? (
        <div
          className={`rounded-lg border px-4 py-10 text-center text-sm ${
            isLight ? 'bg-white border-slate-200 text-slate-500' : 'bg-neutral-900 border-neutral-800 text-slate-400'
          }`}
        >
          No services match the current filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((svc) => (
            <ServiceCard
              key={svc.id}
              service={svc}
              spark={sparkByService[svc.id] || []}
              flipped={flippedIds.has(svc.id)}
              isLight={isLight}
              maxLatency={maxLatency}
            />
          ))}
        </div>
      )}

      {/* Detail table */}
      {status && filtered.length > 0 && (
        <div
          className={`rounded-lg border overflow-hidden ${
            isLight ? 'bg-white border-slate-200' : 'bg-neutral-900 border-neutral-800'
          }`}
        >
          <div className={`px-4 py-2.5 border-b ${isLight ? 'border-slate-100' : 'border-neutral-800'}`}>
            <h3 className={`text-xs font-semibold ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>
              Service detail
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className={isLight ? 'bg-slate-50 text-slate-500' : 'bg-neutral-950 text-slate-400'}>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSort('id')} className="hover:underline">
                      Service {sortKey === 'id' ? (sortAsc ? '↑' : '↓') : ''}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSort('status')} className="hover:underline">
                      Status {sortKey === 'status' ? (sortAsc ? '↑' : '↓') : ''}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">URL</th>
                  <th className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleSort('latency')} className="hover:underline">
                      Latency {sortKey === 'latency' ? (sortAsc ? '↑' : '↓') : ''}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">HTTP</th>
                  <th className="px-3 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((svc) => (
                  <ServiceTableRow key={svc.id} service={svc} isLight={isLight} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function ServiceTableRow({ service, isLight }: { service: HealthService; isLight: boolean }) {
  const Icon = getServiceIcon(service.id);
  return (
    <tr className={`border-t ${isLight ? 'border-slate-100' : 'border-neutral-800'}`}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={13} className={isLight ? 'text-slate-500' : 'text-slate-400'} />
          <div className="min-w-0">
            <p className={`font-medium truncate ${isLight ? 'text-slate-800' : 'text-slate-100'}`}>
              {humanizeServiceId(service.id)}
            </p>
            {service.required && (
              <span className={`text-[9px] uppercase tracking-wide ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
                Required
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-2">
        <span className={statusPillClass(service.status, isLight)}>{service.status}</span>
      </td>
      <td className="px-3 py-2">
        <span
          className={`font-mono text-[10px] truncate max-w-[16rem] block ${isLight ? 'text-slate-500' : 'text-slate-400'}`}
          title={service.url}
        >
          {service.url}
        </span>
      </td>
      <td className={`px-3 py-2 font-mono tabular-nums ${latencyColor(service.latency_ms, isLight)}`}>
        {formatLatency(service.latency_ms)}
      </td>
      <td className={`px-3 py-2 font-mono tabular-nums ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
        {service.http_status ?? '—'}
      </td>
      <td className="px-3 py-2 max-w-[14rem]">
        {service.error ? (
          <span className={`truncate block ${isLight ? 'text-red-600' : 'text-red-400'}`} title={service.error}>
            {service.error}
          </span>
        ) : (
          <span className={isLight ? 'text-slate-300' : 'text-slate-600'}>—</span>
        )}
      </td>
    </tr>
  );
}

export default HealthStatusPage;
